// Admin Finance Tab — Budget lines (planned vs actual) + revenue overview
// Aggregates donations + orders (membership/event/presence) for revenue KPIs
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CAT_LABELS_FR = {
  mecenat: "Mécénat", fresques: "Fresques", logistique: "Logistique",
  communication: "Communication", partenariats: "Partenariats", autre: "Autre",
};

const emptyLine = () => ({ label: "", category: "autre", planned_amount: 0, actual_amount: 0, note: "" });

export default function FinanceTab({ locale }) {
  const isFr = locale === "fr";
  const [budget, setBudget] = useState(null);
  const [orders, setOrders] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, o, d] = await Promise.all([
        api.get("/admin/finance/budget"),
        api.get("/admin/orders"),
        api.get("/admin/donations"),
      ]);
      setBudget(b.data);
      setOrders(Array.isArray(o.data) ? o.data : o.data.items || []);
      setDonations(d.data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeLine = async (line) => {
    if (!window.confirm(isFr ? `Supprimer « ${line.label} » ?` : `Delete "${line.label}"?`)) return;
    await api.delete(`/admin/finance/budget/${line.id}`);
    load();
  };

  // Revenue aggregates
  const revenue = useMemo(() => {
    const paidOrders = orders.filter((o) => o.payment_status === "paid");
    const byKind = { membership: 0, event: 0, presence: 0, donation: 0 };
    paidOrders.forEach((o) => {
      const amt = parseFloat(o.amount || 0);
      if (byKind[o.kind] !== undefined) byKind[o.kind] += amt;
    });
    const donationsPaid = donations.filter((d) => d.payment_status === "paid");
    const donationsTotal = donationsPaid.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
    const donationsPending = donations
      .filter((d) => d.payment_status !== "paid")
      .reduce((s, d) => s + parseFloat(d.amount || 0), 0);
    // Donations may be tracked via /admin/donations OR orders.kind=donation; prefer donations endpoint
    const total = byKind.membership + byKind.event + byKind.presence + donationsTotal;
    return {
      membership: byKind.membership,
      event: byKind.event,
      presence: byKind.presence,
      donations_paid: donationsTotal,
      donations_pending: donationsPending,
      donations_count: donationsPaid.length,
      total,
    };
  }, [orders, donations]);

  return (
    <div data-testid="finance-tab" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">{isFr ? "Finances" : "Finance"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Budget prévisionnel + encaissements." : "Budget + revenues."}
          </p>
        </div>
        <button className="btn btn-primary" data-testid="finance-new-line"
                onClick={() => setEditing(emptyLine())}>
          + {isFr ? "Ligne budgétaire" : "Budget line"}
        </button>
      </div>

      {/* Revenue KPIs */}
      <div data-testid="finance-revenue">
        <div className="deco-rule mb-4">
          <span className="font-caps text-[0.78rem]">
            {isFr ? "Encaissements (SumUp confirmés)" : "Revenue (paid via SumUp)"}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label={isFr ? "Total" : "Total"} value={revenue.total} highlight />
          <KpiCard label={isFr ? "Mécénat" : "Patronage"} value={revenue.donations_paid}
                   sub={isFr ? `${revenue.donations_count} don(s)` : `${revenue.donations_count} donation(s)`} />
          <KpiCard label={isFr ? "Adhésions" : "Memberships"} value={revenue.membership} />
          <KpiCard label={isFr ? "Billetterie" : "Tickets"} value={revenue.event} />
          <KpiCard label={isFr ? "Fêtes 2027" : "Fêtes 2027"} value={revenue.presence} />
        </div>
        {revenue.donations_pending > 0 && (
          <div className="mt-3 font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Dons en attente d'ouverture (BOOKING_OPEN=false) : " : "Pending donations: "}
            <strong style={{ color: "var(--gold)" }}>{revenue.donations_pending.toFixed(2)} €</strong>
          </div>
        )}
      </div>

      {editing && (
        <BudgetEditor isFr={isFr} initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {/* Budget summary by category */}
      {!loading && budget && (
        <div data-testid="finance-budget-summary">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Budget — récap par catégorie" : "Budget — by category"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {budget.by_category.map((c) => {
              const variance = c.actual - c.planned;
              const pct = c.planned > 0 ? Math.min(100, Math.round((c.actual / c.planned) * 100)) : 0;
              return (
                <div key={c.category} className="bg-white border p-4" style={{ borderColor: "var(--border)" }}>
                  <div className="font-caps text-[0.6rem]" style={{ color: "var(--gold)", letterSpacing: "0.18em" }}>
                    {CAT_LABELS_FR[c.category] || c.category}
                  </div>
                  <div className="font-display text-2xl mt-1">{c.actual.toFixed(0)} € <span className="text-sm" style={{ color: "var(--muted)" }}>/ {c.planned.toFixed(0)} €</span></div>
                  <div className="mt-2 h-2 bg-gray-100 rounded overflow-hidden">
                    <div className="h-full" style={{
                      width: `${pct}%`,
                      background: variance > 0 ? "var(--cinnabar)" : "var(--moss)",
                    }} />
                  </div>
                  <div className="font-body text-xs mt-2" style={{ color: variance > 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                    {variance > 0
                      ? `${isFr ? "Dépassement" : "Over"} : +${variance.toFixed(2)} €`
                      : `${isFr ? "Reste" : "Remaining"} : ${Math.abs(variance).toFixed(2)} €`}
                  </div>
                </div>
              );
            })}
            <div className="bg-white border-2 p-4" style={{ borderColor: "var(--gold)" }}>
              <div className="font-caps text-[0.6rem]" style={{ color: "var(--gold)", letterSpacing: "0.18em" }}>
                {isFr ? "TOTAL" : "TOTAL"}
              </div>
              <div className="font-display text-2xl mt-1">{budget.total_actual.toFixed(0)} € <span className="text-sm" style={{ color: "var(--muted)" }}>/ {budget.total_planned.toFixed(0)} €</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Budget lines */}
      {!loading && budget && (
        <div data-testid="finance-budget-lines">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Lignes budgétaires" : "Budget lines"} <span style={{ color: "var(--muted)" }}>· {budget.items.length}</span>
            </span>
          </div>
          {budget.items.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Aucune ligne budgétaire." : "No budget line."}
            </div>
          ) : (
            <div className="overflow-x-auto bg-white border" style={{ borderColor: "var(--border)" }}>
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <th className="text-left p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
                      {isFr ? "LIBELLÉ" : "LABEL"}
                    </th>
                    <th className="text-left p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
                      {isFr ? "CATÉGORIE" : "CATEGORY"}
                    </th>
                    <th className="text-right p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
                      {isFr ? "PRÉVU" : "PLANNED"}
                    </th>
                    <th className="text-right p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
                      {isFr ? "RÉEL" : "ACTUAL"}
                    </th>
                    <th className="text-right p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
                      Δ
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {budget.items.map((line) => {
                    const variance = (line.actual_amount || 0) - (line.planned_amount || 0);
                    return (
                      <tr key={line.id} data-testid={`budget-line-${line.id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                        <td className="p-3 font-body">
                          {line.label}
                          {line.note && <div className="text-xs italic" style={{ color: "var(--muted)" }}>{line.note}</div>}
                        </td>
                        <td className="p-3 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
                          {CAT_LABELS_FR[line.category] || line.category}
                        </td>
                        <td className="p-3 text-right font-mono">{(line.planned_amount || 0).toFixed(2)} €</td>
                        <td className="p-3 text-right font-mono">{(line.actual_amount || 0).toFixed(2)} €</td>
                        <td className="p-3 text-right font-mono" style={{ color: variance > 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                          {variance >= 0 ? "+" : ""}{variance.toFixed(2)} €
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <button className="btn btn-outline btn-xs mr-1" data-testid={`budget-edit-${line.id}`}
                                  onClick={() => setEditing({ ...line })}>
                            {isFr ? "Modifier" : "Edit"}
                          </button>
                          <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                                  data-testid={`budget-delete-${line.id}`}
                                  onClick={() => removeLine(line)}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, highlight }) {
  return (
    <div className="admin-card text-center" style={highlight ? { borderColor: "var(--gold)", borderWidth: 2 } : undefined}>
      <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="font-display text-2xl">{(value || 0).toFixed(0)} €</div>
      {sub && <div className="font-caps text-[0.55rem] mt-1" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>{sub}</div>}
    </div>
  );
}

function BudgetEditor({ initial, isFr, onCancel, onSaved }) {
  const [b, setB] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setB((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setErr("");
    if (!b.label.trim()) { setErr(isFr ? "Libellé requis." : "Label required."); return; }
    setBusy(true);
    try {
      const payload = {
        label: b.label, category: b.category,
        planned_amount: parseFloat(b.planned_amount || 0),
        actual_amount: parseFloat(b.actual_amount || 0),
        note: b.note,
      };
      if (b.id) await api.patch(`/admin/finance/budget/${b.id}`, payload);
      else await api.post("/admin/finance/budget", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="budget-editor">
      <h3 className="font-display text-xl">
        {b.id ? (isFr ? "Modifier la ligne" : "Edit line") : (isFr ? "Nouvelle ligne" : "New line")}
      </h3>
      <label className="block">
        <span className="label-caps">{isFr ? "Libellé *" : "Label *"}</span>
        <input className="input-classic mt-1" value={b.label} onChange={(e) => set("label", e.target.value)} data-testid="budget-label" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Catégorie" : "Category"}</span>
          <select className="input-classic mt-1" value={b.category} onChange={(e) => set("category", e.target.value)} data-testid="budget-cat">
            {Object.entries(CAT_LABELS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Montant prévu (€)" : "Planned (€)"}</span>
          <input type="number" step="0.01" className="input-classic mt-1"
                 value={b.planned_amount} onChange={(e) => set("planned_amount", e.target.value)} data-testid="budget-planned" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Montant réel (€)" : "Actual (€)"}</span>
          <input type="number" step="0.01" className="input-classic mt-1"
                 value={b.actual_amount} onChange={(e) => set("actual_amount", e.target.value)} data-testid="budget-actual" />
        </label>
      </div>
      <label className="block">
        <span className="label-caps">{isFr ? "Note (optionnel)" : "Note (optional)"}</span>
        <input className="input-classic mt-1" value={b.note} onChange={(e) => set("note", e.target.value)} data-testid="budget-note" />
      </label>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="budget-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="budget-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}
